import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Activity, BarChart3, LineChart, TrendingUp, Eye, Target,
  Gauge, RefreshCw, FileText, MapPin, DollarSign, Footprints, PieChart, Sparkles
} from "lucide-react";
import { SEO } from "../components/SEO";

const MeasurementPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="measurement-page">
      <SEO
        title="Measurement & Reporting"
        description="Real business metrics — visits, conversions, engagement, and ROI. SmartReach™ measurement tracks campaigns to actual customer behavior, not vanity metrics."
        path="/measurement"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Activity size={16} /> Real Business Metrics
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Measurement That Tracks <span className="text-gradient-orange">Real Business Outcomes</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            Visits, conversions, engagement, repeat rate — the metrics that move your P&L. See exactly
            what's working and where to invest next.
          </p>
          <button onClick={() => navigate("/contact")} className="btn-primary text-lg">
            See A Sample Report <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>

      {/* REAL BUSINESS METRICS */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Real Business Metrics</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              No more vanity metrics. We measure what your accountant cares about.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Footprints, title: "Visits", desc: "Anonymized location signals confirm how many people walked into your store after seeing your campaign." },
              { icon: Target, title: "Conversions", desc: "Where measurable, we tie ad exposures to redemptions, transactions, and bookings." },
              { icon: TrendingUp, title: "Engagement", desc: "Loyalty interactions, app sessions, and repeat-visit rate — proxies for long-term customer value." }
            ].map((m, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-light to-white p-8 rounded-2xl border border-orange-primary/10 card-hover" data-testid={`metric-card-${idx}`}>
                <m.icon className="text-orange-primary mb-4" size={40} />
                <h3 className="font-display font-bold text-2xl text-gray-dark mb-3">{m.title}</h3>
                <p className="text-gray-medium">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD MOCKUP */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Reporting You Can <span className="text-gradient-orange">Actually Read</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              One dashboard. The numbers that matter. No noise.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-dark to-gray-900 rounded-3xl p-6 md:p-10">
            <div className="bg-white rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                <div>
                  <p className="text-xs text-gray-medium">SmartReach™ Dashboard</p>
                  <h3 className="font-display font-bold text-gray-dark">Last 30 Days</h3>
                </div>
                <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">LIVE</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Total Visits", value: "12,847", change: "+38%", icon: Footprints },
                  { label: "Cost / Visit", value: "$2.41", change: "-22%", icon: DollarSign },
                  { label: "Repeat Rate", value: "47%", change: "+12pt", icon: RefreshCw },
                  { label: "Visit Lift", value: "+31%", change: "vs baseline", icon: TrendingUp }
                ].map((kpi, idx) => (
                  <div key={idx} className="bg-orange-light/40 rounded-xl p-4">
                    <kpi.icon className="text-orange-primary mb-2" size={18} />
                    <p className="text-xs text-gray-medium">{kpi.label}</p>
                    <p className="text-2xl font-display font-bold text-gray-dark">{kpi.value}</p>
                    <p className="text-xs text-orange-primary font-semibold mt-1">{kpi.change}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-light rounded-xl p-4">
                  <p className="text-xs text-gray-medium mb-3">Channel Mix → Visits</p>
                  {[
                    { ch: "Radio", pct: 35 },
                    { ch: "Mobile", pct: 28 },
                    { ch: "Social", pct: 18 },
                    { ch: "DOOH", pct: 12 },
                    { ch: "CTV", pct: 7 }
                  ].map((c, i) => (
                    <div key={i} className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-dark font-medium">{c.ch}</span>
                        <span className="text-gray-medium">{c.pct}%</span>
                      </div>
                      <div className="h-2 bg-white rounded-full overflow-hidden">
                        <div className="h-full gradient-orange rounded-full" style={{ width: `${c.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-light rounded-xl p-4">
                  <p className="text-xs text-gray-medium mb-3">Visits By Location</p>
                  {[
                    { loc: "Plano", val: "4,120" },
                    { loc: "Frisco", val: "3,540" },
                    { loc: "Irving", val: "2,890" },
                    { loc: "Dallas Downtown", val: "2,297" }
                  ].map((l, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/50 last:border-0">
                      <span className="text-sm text-gray-dark flex items-center gap-2">
                        <MapPin size={14} className="text-orange-primary" /> {l.loc}
                      </span>
                      <span className="text-sm font-semibold text-gray-dark">{l.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400 mt-4 italic">* Illustrative dashboard — your numbers will be unique to your business.</p>
          </div>
        </div>
      </section>

      {/* INSIGHTS & TRENDS */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Insights & <span className="text-gradient-orange">Trend Analysis</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: BarChart3, title: "Dashboards", desc: "Live performance views — visits, lift, channel mix, location breakdown — refreshed automatically." },
              { icon: Eye, title: "Insights", desc: "Plain-language interpretations of what's working, what's not, and what to do next." },
              { icon: LineChart, title: "Trends", desc: "Week-over-week and month-over-month trend analysis with seasonality and benchmark comparisons." }
            ].map((it, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card border border-gray-100 card-hover">
                <it.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{it.title}</h3>
                <p className="text-gray-medium text-sm">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPTIMIZATION */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Optimization → <span className="text-gradient-orange">Better ROI Every Cycle</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Measurement isn't an end in itself — it's the input to a continuously improving system.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: "Campaign Adjustments", desc: "Daily and weekly tuning of audiences, creatives, dayparts, and budget allocation based on what's working." },
              { icon: Gauge, title: "Performance Loop", desc: "Underperformers get cut; winners get scaled. The system learns your business over time." },
              { icon: PieChart, title: "Better ROI", desc: "Cost per visit drops. Visit lift rises. Revenue grows — proven on the dashboard, not promised." }
            ].map((o, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <o.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{o.title}</h3>
                <p className="text-gray-medium text-sm">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding gradient-orange">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            See your real numbers — not just promises.
          </h2>
          <button onClick={() => navigate("/contact")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            Book a Demo <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default MeasurementPage;
